import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Typography,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";
import LandingOptionsAdmin from "./landingOptions";

function ServiceOfferingsManager() {
  const { t } = useTranslation();
  const [offerings, setOfferings] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentOffering, setCurrentOffering] = useState({
    title: "",
    description: "",
    offering_type: "Service", // Valor predeterminado
    icon_name: "",
    button_text: "",
    full_description: "",
    benefits: [],
    image_url: "",
  });

  const iconOptions = [
    { value: "Elevator", label: "Elevator" },
    { value: "Build", label: "Build" },
    { value: "Security", label: "Security" },
  ];

  const offeringTypeOptions = [
    { value: "service", label: "Service" },
    { value: "news", label: "News" },
    { value: "interest", label: "Interest" },
  ];

  useEffect(() => {
    fetchOfferings();
  }, []);

  const fetchOfferings = async () => {
    try {
      const response = await axiosInstance.get("/service_offerings");
      setOfferings(response.data);
    } catch (error) {
      console.error("Error fetching service offerings:", error);
    }
  };

  const handleOpen = (offering = null) => {
    const defaultOffering = {
      title: "",
      description: "",
      offering_type: "Service", // Valor predeterminado
      icon_name: "",
      button_text: "",
      full_description: "",
      benefits: [],
      image_url: "",
    };

    if (offering) {
      // Transformar icon_name: primera letra mayúscula y el resto minúscula
      const formattedIcon =
        offering.icon_name &&
        (offering.icon_name.charAt(0).toUpperCase() +
          offering.icon_name.slice(1).toLowerCase());
      offering.icon_name = formattedIcon || "";
      setCurrentOffering(offering);
    } else {
      setCurrentOffering(defaultOffering);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentOffering(null);
  };

  const handleSave = async () => {
    try {
      if (currentOffering.id) {
        await axiosInstance.put(
          `/service_offerings/${currentOffering.id}`,
          { service_offering: currentOffering }
        );
      } else {
        await axiosInstance.post("/service_offerings", {
          service_offering: currentOffering,
        });
      }
      fetchOfferings();
      handleClose();
    } catch (error) {
      console.error("Error saving offering:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("areYouSureDeleteOffering"))) {
      try {
        await axiosInstance.delete(`/service_offerings/${id}`);
        fetchOfferings();
      } catch (error) {
        console.error("Error deleting offering:", error);
      }
    }
  };

  // Función para leer la imagen y almacenarla en el estado en formato base64
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentOffering({
          ...currentOffering,
          image_url: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4">{t("manageServiceOfferings")}</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{ backgroundColor: "#29474d" }}
          >
            {t("addNewService")}
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("title")}</TableCell>
                <TableCell>{t("description")}</TableCell>
                <TableCell>{t("type")}</TableCell>
                <TableCell>{t("icon")}</TableCell>
                <TableCell>{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offerings.map((offering) => (
                <TableRow key={offering.id}>
                  <TableCell>{offering.title}</TableCell>
                  <TableCell>{offering.description}</TableCell>
                  <TableCell>{offering.offering_type}</TableCell>
                  <TableCell>{offering.icon_name}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpen(offering)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(offering.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
          <DialogTitle>
            {currentOffering?.id
              ? t("editServiceOffering")
              : t("newServiceOffering")}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("title")}
                  value={currentOffering?.title || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      title: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("description")}
                  value={currentOffering?.description || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      description: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Select
                  fullWidth
                  value={currentOffering?.offering_type || "service"}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      offering_type: e.target.value,
                    })
                  }
                  displayEmpty
                >
                  {offeringTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t("icon")}
                  value={currentOffering?.icon_name || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      icon_name: e.target.value,
                    })
                  }
                >
                  {iconOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                  {/* Opción adicional en caso de que el icono actual no se encuentre en las opciones */}
                  {currentOffering?.icon_name &&
                    !iconOptions.some(
                      (option) => option.value === currentOffering.icon_name
                    ) && (
                      <MenuItem value={currentOffering.icon_name}>
                        {currentOffering.icon_name}
                      </MenuItem>
                    )}
                </TextField>
              </Grid>
              {/* Campo para button_text */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("buttonText")}
                  value={currentOffering?.button_text || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      button_text: e.target.value,
                    })
                  }
                />
              </Grid>
              {/* Campo para full_description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("fullDescription")}
                  multiline
                  rows={4}
                  value={currentOffering?.full_description || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      full_description: e.target.value,
                    })
                  }
                />
              </Grid>
              {/* Campo para benefits (separados por comas) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("benefits")}
                  value={currentOffering?.benefits.join(", ") || ""}
                  onChange={(e) =>
                    setCurrentOffering({
                      ...currentOffering,
                      benefits: e.target.value
                        .split(",")
                        .map((item) => item.trim()),
                    })
                  }
                />
              </Grid>
              {/* Campo para subir imagen */}
              <Grid item xs={12}>
                <Button variant="contained" component="label">
                  {t("uploadImage")}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
              </Grid>
              {/* Vista previa de la imagen, si existe */}
              {currentOffering?.image_url && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <img
                      src={currentOffering.image_url}
                      alt="Preview"
                      style={{ maxWidth: "100%", maxHeight: "200px" }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t("cancel")}</Button>
            <Button onClick={handleSave} variant="contained">
              {currentOffering?.id ? t("update") : t("create")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Box>
        <LandingOptionsAdmin />
      </Box>
    </>
  );
}

export default ServiceOfferingsManager;
